import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import { useEvent } from "../../hooks/useEvent";
import { dispatchChangeEvent } from "../../utils/dispatchChangeEvent";
import { ErrorLike } from "../../PropX";
import { applySuggestion } from "./applySuggestion";
import { findOptions } from "./findOptions";
import { normalizeSelection, Selection } from "./selection";
import { Suggestions } from "./suggestions";
import { validateSource } from "./validateSource";

type Props = {
  suggestions: Suggestions;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
};

type Config = {
  ref: React.RefObject<HTMLInputElement>;
};

export const useSuggestionsProgram = (props: Props, config: Config) => {
  const { ref } = config;
  const { suggestions } = props;

  const [error, setError] = React.useState<ErrorLike | undefined>(undefined);
  const [selection, setSelection] = React.useState<Selection>({ start: 0, end: 0 });

  React.useLayoutEffect(() => {
    const input = ref.current;

    if (!input) {
      return;
    }

    const handleSelection = () => {
      /**
       * We need to defer the selection update to the next tick to ensure
       * that the selection is updated before we try to find the options.
       */
      batch(() => {
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const next = normalizeSelection({ start, end });
        setSelection(next);
      });
    };

    input.addEventListener("keyup", handleSelection);
    input.addEventListener("mouseup", handleSelection);

    return () => {
      input.removeEventListener("keyup", handleSelection);
      input.removeEventListener("mouseup", handleSelection);
    };
  }, [ref.current]);

  const options = React.useMemo(() => {
    const source = ref.current?.value ?? "";

    try {
      return findOptions(source, selection, suggestions);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error(err);
      }

      return [];
    }
  }, [ref.current?.value, selection, suggestions]);

  const onSelect = useEvent((value: string): void => {
    const input = ref.current;

    if (!input) {
      return;
    }

    try {
      const next = applySuggestion(input.value, selection, suggestions, value);

      batch(() => {
        dispatchChangeEvent(input, next.source);
        /**
         * Here we deliberately set the selection range to the same value as
         * it was before to avoid it jumping to the end of the input.
         */
        input.setSelectionRange(next.selection.start, next.selection.end);
      });

      input.focus();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error(err);
      }

      return;
    }
  });

  const onChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((ev): void => {
    batch(() => {
      setError(undefined);
      props.onChange?.(ev);
    });
  });

  const onBlur = useEvent<React.FocusEventHandler<HTMLElement>>((ev): void => {
    const input = ref.current;

    if (!input) {
      return;
    }

    batch(() => {
      try {
        const next = validateSource(input.value, suggestions);

        if (next !== input.value) {
          dispatchChangeEvent(input, next);
        }

        setError(undefined);
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        } else {
          setError({ message: "Unknown error" });
        }
      }
      props.onBlur?.(ev);
    });
  });

  return { options, error, onSelect, onChange, onBlur };
};
